import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import MaterialReactTable, { MRT_RowSelectionState, MRT_ToolbarAlertBanner as ToolBarAlertBanner } from "material-react-table";
import { Box, Button, Stack, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Tooltip } from "@mui/material";
import dayjs from "dayjs";

import { EsgDeleteIcon, EsgDownloadIcon } from "assets";
import { SiteDTO, deleteUtilityBill, downloadUtilityBills, getSavedSearchFilters, getSavedSearchFiltersList } from "api";
import {
  objectToColumnFilters,
  useNotify,
  usePagination,
  useSites,
  useTableFilters,
  useUtilityBillsYears,
} from "hooks";
import { useSaveCurrentUrl, useSetDefaultUtilityBillsSelection, useUserRole } from "recoils";
import { downloadFile, extractErrorMessage, getDateFormatset } from "utils";
import { LOCAL_STORAGE_KEYS, QUERY_KEYS } from "consts";
import {
  ActiveFilters,
  DeleteDialog,
  EsgDatePickerField,
  InfoLabel,
  Loading,
  MultiSelectControlledOption,
  Pagination,
  Switch,
  useDialogControls,
  useTableSorting,
} from "components";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";

import { UTILITY_BILLS_TABLE } from "./consts";
import { getMuiTableBodyRowProps } from "./helpers";
import { filterParamsToQuery } from "./helpers/filterParamsToQuery";
import { createDocumentSummaryFilterConfig, DocumentSummaryFilters } from "./filter-config";
import { ExportButtonWrapper, getBillEntriesColums, injectFilterTextFieldProps } from "./components";
import { useFetchBillsData } from "./hooks";

export const UtilityBillsPage = () => {
  // ---- Hooks
  const notify = useNotify();
  const navigate = useNavigate();
  const currentUrl = useLocation();
  const queryClient = useQueryClient();
  const saveCurrentUrl = useSaveCurrentUrl();
  const setDefaultSelection = useSetDefaultUtilityBillsSelection();
  const deleteDialogControls = useDialogControls();
  const userRole = useUserRole();
  const { data: availableYears, isLoading: loadingAvailableYears } = useUtilityBillsYears();
  const { data: sitesData } = useSites();

  const tableInstanceRef = useRef<any>(null);
  const rowVirtualizerInstanceRef = useRef<any>(null);
  const isUserPaginationChange = useRef<boolean>(false);

  // ---- Const
  // Local Storage
  const storedStartDate = localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_START_DATE);
  const storedEndDate = localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_END_DATE);
  const storedSitesFilter = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_SITES_FILTER) || "[]");
  const storedIsEdited = localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_IS_EDITED) === "true";
  const storedIsMyTask = localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_IS_MY_TASK) === "true";
  const storedShowDuplicates = localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_SHOW_DUPLICATES) === "true";

  const summaryDocumentID = window.sessionStorage.getItem("summaryDocumentID");
  const url = new URL(window.location.href);
  const queryName = url.pathname;

  const dateFormat = getDateFormatset("/");

  // ---- API Call's
  const { sortBy, sortDirection } = useTableSorting(UTILITY_BILLS_TABLE.COLUMN_TO_SORT_COLUMN_MAP, UTILITY_BILLS_TABLE.INITIAL_SORTING_STATE);

  const {
    pagination,
    paginatedQuery,
    onPageChange,
    onSizeChange,
    updateFromApiResponse,
    resetPage,
    uiPage,
  } = usePagination({
    localStorageKey: LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_PAGINATION,
    initialPage: 0,
    initialSize: 100,
  });

  const { size, page, totalRows } = pagination;

  const { mutateAsync: deleteUB } = useMutation(deleteUtilityBill);

  const { mutateAsync: getSavedFilterValue, data: savedFilterValueResponse } =
    useMutation(getSavedSearchFiltersList);

  const { mutateAsync: getSavedFilterListMutation, data: savedFilterData } =
    useMutation(getSavedSearchFilters);

  const getSavedFilterList = useCallback(() => {
    getSavedFilterListMutation();
  }, [getSavedFilterListMutation]);

  // ---- Column filters (apply on Search / Enter / arrow, like document-upload)
  const filterConfig = useMemo(() => createDocumentSummaryFilterConfig(), []);
  const tableFilters = useTableFilters<DocumentSummaryFilters>({
    config: filterConfig,
    pagination: { onPageChange, resetPage: () => onPageChange(1) },
  });
  const mrtFilterProps = tableFilters.getMRTFilterProps();
  const activeFiltersProps = tableFilters.getActiveFiltersProps();

  // ---- States
  const [showAnomalyColumn, setShowAnomalyColumn] = useState<boolean>(false);
  const [showDuplicateEntries, setShowDuplicateEntries] = useState<boolean>(storedShowDuplicates);
  const [isEdited, setIsEdited] = useState<boolean>(storedIsEdited);
  const [isMyTask, setIsMyTask] = useState<boolean>(storedIsMyTask);

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState<string>("");

  const [editFilterIndex, setEditFilterIndex] = useState<number>(-1);
  const [startDate, setStartDate] = useState<string>(
    storedStartDate || dayjs().month(0).startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState<string>(
    storedEndDate || dayjs().format("YYYY-MM-DD")
  );
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [filterName, setFilterName] = useState<string>("");
  const [allowSearch, setAllowSearch] = useState<boolean>(true);
  const [currentSiteIds, setCurrentSiteIds] = useState<MultiSelectControlledOption[]>(storedSitesFilter);
  const [minDate, setMinDate] = useState<dayjs.Dayjs | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  const sites = useMemo(() => sitesData ? sitesData.map((site: SiteDTO) => ({ name: site.name, label: site.name, value: site.id })) : [], [sitesData]);

  const buildQueryObject = useCallback((overrides = {}) => ({
    isEdited,
    isMyTask,
    areDuplicates: showDuplicateEntries,
    siteIds: currentSiteIds.map((site) => site.value as number),
    startDate,
    endDate,
    sortColumn: sortBy,
    direction: sortDirection,
    ...paginatedQuery,
    ...overrides
  }), [
    isEdited,
    isMyTask,
    showDuplicateEntries,
    currentSiteIds,
    startDate,
    endDate,
    sortBy,
    sortDirection,
    paginatedQuery,
  ]);

  const queryObject = useMemo(
    () => ({
      ...buildQueryObject(),
      ...filterParamsToQuery(tableFilters.getQueryParams() as Record<string, unknown>),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildQueryObject, tableFilters.filters]
  );

  const {
    fetchBillsData,
    loading,
    loadingBills,
    utilityBillsData,
    duplicateData,
  } = useFetchBillsData({
    queryObject,
    updateFromApiResponse,
  });

  const tableData = useMemo(() => {
    const data = showDuplicateEntries ? duplicateData : utilityBillsData?.bills || [];
    return [...data];
  }, [showDuplicateEntries, duplicateData, utilityBillsData?.bills]);

  const onPageChangeHandler = (newPage: number) => {
    isUserPaginationChange.current = true;
    onPageChange(newPage);
  };

  const onPageSizeChangeHandler = (newPageSize: number) => {
    isUserPaginationChange.current = true;
    onSizeChange(newPageSize);
  };

  const handleDateChange = (date: string, dateType: "startDate" | "endDate") => {
    const newDate = dayjs(date);

    if (newDate.isValid()) {
      const formattedDate = newDate.format("YYYY-MM-DD");
      if (dateType === "startDate") {
        setStartDate(formattedDate);
        localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_START_DATE, formattedDate);
      } else {
        setEndDate(formattedDate);
        localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_END_DATE, formattedDate);
      }
    }
  };

  const handleSetSites = (values: MultiSelectControlledOption[]) => {
    setCurrentSiteIds(values);
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_SITES_FILTER, JSON.stringify(values));
  };

  const handleSearch = useCallback(() => {
    tableFilters.applyFilters();
    onPageChange(1);
  }, [tableFilters, onPageChange]);

  // ---- Table Data and Columns (inject filter text field props so arrow button appears in each column with grouped headers)
  const columnsTableDefinition = getBillEntriesColums({ currentUrl });
  const columnsWithFilterProps = useMemo(
    () => injectFilterTextFieldProps(columnsTableDefinition, mrtFilterProps.muiTableHeadCellFilterTextFieldProps as any),
    [columnsTableDefinition, mrtFilterProps.muiTableHeadCellFilterTextFieldProps]
  );
  const columnsTableData = showAnomalyColumn ? columnsWithFilterProps : columnsWithFilterProps.filter((item: any) => item.id !== "anomalies");

  const onResetAll = async () => {
    const resetStartDate = dayjs().month(0).startOf("month").format("YYYY-MM-DD");
    const resetEndDate = dayjs().format("YYYY-MM-DD");

    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_START_DATE, resetStartDate);
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_END_DATE, resetEndDate);
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_SITES_FILTER, JSON.stringify([]));
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_IS_EDITED, "false");
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_IS_MY_TASK, "false");
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_SHOW_DUPLICATES, "false");
    setShowDuplicateEntries(false);
    tableFilters.clearFilters();
    setIsEdited(false);
    setIsMyTask(false);
    setEditFilterIndex(-1);
    setCurrentSiteIds([]);
    setStartDate(resetStartDate);
    setEndDate(resetEndDate);
    setRowSelection({});

    isUserPaginationChange.current = false;
    onPageChange(1);

    await fetchBillsData({
      billType: 'utility',
      queryOverrides: {
        startDate: resetStartDate,
        endDate: resetEndDate,
        siteIds: [],
        page: 0,
      },
    });
  };

  const handleSaveButton = () => {
    const appliedColumnFilters = objectToColumnFilters(
      tableFilters.filters,
      filterConfig.columnIdMap
    );
    const columnData = {
      key: filterName,
      value: appliedColumnFilters,
    };

    if (editFilterIndex !== -1) {
      const filterValues = localData !== undefined ? localData : [];

      const index = editFilterIndex;
      if (index > -1) {
        filterValues.splice(index, 1);
      }

      const finalValue = [...filterValues, columnData];
      if (finalValue) {
        getSavedFilterValue({
          queryName,
          searchFilters: finalValue,
        });
      }
    } else {
      const filterValues = localData !== undefined ? localData : [];
      const finalValue = [...filterValues, columnData];

      if (finalValue) {
        getSavedFilterValue({
          queryName,
          searchFilters: finalValue,
        });
      }
    }
    setOpenDialog(false);
    setEditFilterIndex(-1);
    setFilterName("");
  };

  const localData = savedFilterData && savedFilterData[0]?.searchFilters;
  const selected = Object.keys(rowSelection);

  const onDownloadClick = () => {
    if (isDownloading) return;
    setIsDownloading(true);
    downloadFile(() => downloadUtilityBills(selected), "utility-bills.zip")
      .then(() => notify.success("Selected utility bills were successfully downloaded!"))
      .catch((error) => notify.error(extractErrorMessage(error, "Failed to download selected utility bills!")))
      .finally(() => setIsDownloading(false));
  };

  const onDeleteClick = () => {
    Promise.allSettled(
      selected.map((id) =>
        deleteUB(id, {
          onSuccess: () => {
            notify.success("Selected utility bills were deleted!");
            setRowSelection({});
            queryClient.invalidateQueries([QUERY_KEYS.UTILITY_BILLS]);
          },
          onError: (error: any) => {
            notify.error(error?.response?.data && typeof error?.response?.data === "string"
              ? error?.response?.data
              : `Some error has happen while deleting utility bill: ${id}!`,
            )
          }
        }),
      ),
    ).finally(() => {
      deleteDialogControls.close();
      setDefaultSelection();
      const billType = showDuplicateEntries ? 'duplicate' : (tableFilters.hasActiveFilters ? 'filtered' : 'utility');
      fetchBillsData({ billType });
    });
  };

  // ---- Use Effects
  useEffect(() => {
    getSavedFilterList();
  }, [getSavedFilterList]);

  useEffect(() => {
    if (savedFilterValueResponse) {
      getSavedFilterList();
    }
  }, [savedFilterValueResponse, getSavedFilterList]);

  useEffect(() => {
    if (tableInstanceRef?.current && summaryDocumentID) {
      const selectedIndex = utilityBillsData?.bills.findIndex(
        (row: any) => row.id === summaryDocumentID,
      );
      if (selectedIndex && selectedIndex !== -1) {
        const elementToScroll = rowVirtualizerInstanceRef.current;

        elementToScroll.scrollToIndex(selectedIndex + 10);
      }
    }
  }, [summaryDocumentID]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (!start.isValid() || !end.isValid()) {
      setAllowSearch(false);
      return;
    }

    const dateValidation = end.isBefore(start);
    setAllowSearch(!dateValidation);
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isUserPaginationChange.current) {
      return;
    }
    isUserPaginationChange.current = false;
    const billType = showDuplicateEntries ? 'duplicate' : (tableFilters.hasActiveFilters ? 'filtered' : 'utility');
    fetchBillsData({ billType, queryOverrides: { page } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size]);

  // Fetch when query (including column filters) or showDuplicateEntries changes.
  useEffect(() => {
    const billType = showDuplicateEntries ? 'duplicate' : (tableFilters.hasActiveFilters ? 'filtered' : 'utility');
    fetchBillsData({ billType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryObject, showDuplicateEntries]);

  // Force virtualizer to recalculate when data changes
  useEffect(() => {
    if (!loadingBills && rowVirtualizerInstanceRef.current && tableData.length > 0) {
      // Access the virtualizer and force it to measure rows
      const virtualizer = rowVirtualizerInstanceRef.current;

      virtualizer.measure?.();
    }
  }, [loadingBills, tableData, showDuplicateEntries]);

  useEffect(() => {
    if (availableYears && availableYears.length > 0) {
      setMinDate(dayjs(`${availableYears[0]}-01-01`));
    }
  }, [availableYears]);

  return (
    loadingAvailableYears ? <Loading /> : (
      <Box sx={{ marginLeft: 3, marginRight: 3, pt: 1.5 }}>
        <Box sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}>
          <Stack direction="row" spacing={2}>
            <EsgDatePickerField
              width="160px"
              label={<InfoLabel
                title="Start Date"
                tooltipSx={{
                  maxWidth: "180px",
                  height: "fit-content",
                  fontSize: "10px",
                  textAlign: "center",
                }}
                labelSx={{
                  width: "100%",
                  height: "18px",
                  padding: "0px",
                  marginBottom: "4px",
                  flexDirection: "row-reverse",
                  justifyContent: "flex-end",
                  backgroundColor: "transparent",
                  "& > svg": {
                    width: "14px",
                    height: "14px",
                    color: "black",
                  }
                }}
                titleSx={{
                  color: "black",
                  fontSize: "12px",
                }}
                infoMessage="Filter to bills where any part of the bill period overlaps with the selected date range"
              />}
              views={["year", "month", "day"]}
              openTo="year"
              format={dateFormat}
              disableHighlightToday
              onAccept={(e: any) => handleDateChange(e, "startDate")}
              onChange={(e: any) => handleDateChange(e, "startDate")}
              setDate={(e: any) => handleDateChange(e, "startDate")}
              value={dayjs(startDate)}
              placeholdervalue={dateFormat}
              filterAll={false}
              clearDate={false}
              minDate={minDate}
              maxDate={dayjs()}
            />
            <EsgDatePickerField
              width="160px"
              label={<InfoLabel
                title="End Date"
                tooltipSx={{
                  maxWidth: "180px",
                  height: "fit-content",
                  fontSize: "10px",
                  textAlign: "center",
                }}
                labelSx={{
                  width: "100%",
                  height: "18px",
                  padding: "0px",
                  marginBottom: "4px",
                  flexDirection: "row-reverse",
                  justifyContent: "flex-end",
                  backgroundColor: "transparent",
                  "& > svg": {
                    width: "14px",
                    height: "14px",
                    color: "black",
                  }
                }}
                titleSx={{
                  color: "black",
                  fontSize: "12px",
                }}
                infoMessage="Filter to bills where any part of the bill period overlaps with the selected date range"
              />}
              views={["year", "month", "day"]}
              openTo="year"
              format={dateFormat}
              disableHighlightToday
              onAccept={(e: any) => handleDateChange(e, "endDate")}
              onChange={(e: any) => handleDateChange(e, "endDate")}
              setDate={(e: any) => handleDateChange(e, "endDate")}
              value={dayjs(endDate)}
              placeholdervalue={dateFormat}
              filterAll={false}
              clearDate={false}
              minDate={minDate}
              maxDate={dayjs()}
            />
          </Stack>
          <Box sx={{ width: "180px" }}>
            <EsgMultiselectControlledField
              label="Sites"
              selectedValues={currentSiteIds}
              setSelectedValues={handleSetSites}
              options={sites as MultiSelectControlledOption[]}
              type="checkMissing"
              isAllSelectedOnEmptySelection
            />
          </Box>
          <Box
            sx={{
              width: "296px !important",
              display: "flex",
              gap: "16px",
              mt: "22px",
            }}
          >
            <Button
              sx={{
                width: "140px",
                height: "36px",
                borderRadius: "8px",
              }}
              disabled={!allowSearch}
              variant="contained"
              onClick={() => handleSearch()}
            >
              Search
            </Button>
            <Button
              sx={{
                width: "140px",
                height: "36px",
                borderRadius: "8px",
              }}
              variant="outlined"
              onClick={onResetAll}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
        <Box sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Switch
              isChecked={isEdited}
              label="Show only edited"
              onChange={async () => {
                const isShowingEdited = !isEdited;
                setRowSelection({});
                setIsEdited(isShowingEdited);
                localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_IS_EDITED, String(isShowingEdited));
                onPageChange(1);
                await fetchBillsData({
                  billType: showDuplicateEntries ? 'duplicate' : 'utility',
                  queryOverrides: {
                    ...queryObject,
                    isEdited: isShowingEdited,
                    page: 0,
                  },
                });
              }}
            />
            <Switch
              isChecked={isMyTask}
              label="Show my tasks"
              onChange={async () => {
                const isShowingMyTasks = !isMyTask;
                setRowSelection({});
                setIsMyTask(isShowingMyTasks);
                localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_IS_MY_TASK, String(isShowingMyTasks));
                onPageChange(1);
                await fetchBillsData({
                  billType: showDuplicateEntries ? 'duplicate' : 'utility',
                  queryOverrides: {
                    ...queryObject,
                    isMyTask: isShowingMyTasks,
                    page: 0,
                  },
                });
              }}
            />
            <Switch
              isChecked={showAnomalyColumn}
              label="Show anomaly column"
              onChange={setShowAnomalyColumn}
            />
            <Tooltip title="This toggle will show all cases where the account #, meter #, start date, end date, usage, and cost are the same">
              <div>
                <Switch
                  isChecked={showDuplicateEntries}
                  label="Show duplicate entries"
                  onChange={async () => {
                    const isShowingDuplicates = !showDuplicateEntries;
                    setRowSelection({});
                    setShowDuplicateEntries(isShowingDuplicates);
                    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_SUMMARY_PAGE_SHOW_DUPLICATES, String(isShowingDuplicates));
                    onPageChange(1);
                    await fetchBillsData({
                      billType: isShowingDuplicates ? 'duplicate' : 'utility',
                      queryOverrides: {
                        ...queryObject,
                        areDuplicates: isShowingDuplicates,
                        page: 0,
                      },
                    });
                  }}
                />
              </div>
            </Tooltip>
          </Box>
          <ExportButtonWrapper
            tableData={tableData}
            queryParams={queryObject}
            startDate={startDate}
            endDate={endDate}
            onFilterChange={resetPage}
          />
        </Box>
        {loadingBills
          ? <Loading />
          : <>
            <Box sx={{ display: "flex", flexDirection: "column", width: "100%", marginTop: "20px" }}>
              <ActiveFilters
                appliedFilters={activeFiltersProps.appliedFilters}
                onClearFilter={(filterId) => activeFiltersProps.onClearFilter(filterId)}
                displayNameMap={activeFiltersProps.displayNameMap}
              />
            </Box>
            <MaterialReactTable
              tableInstanceRef={tableInstanceRef}
              rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
              enableTopToolbar
              enableStickyHeader
              enableColumnResizing
              enableRowVirtualization
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              data={tableData}
              columns={columnsTableData}
              enableRowSelection
              enablePagination
              manualPagination
              rowCount={totalRows}
              onRowSelectionChange={setRowSelection}
              enableColumnFilters={mrtFilterProps.enableColumnFilters}
              manualFiltering={mrtFilterProps.manualFiltering}
              onColumnFiltersChange={mrtFilterProps.onColumnFiltersChange}
              muiTableHeadCellFilterTextFieldProps={mrtFilterProps.muiTableHeadCellFilterTextFieldProps}
              initialState={{ density: "compact", columnFilters: mrtFilterProps.initialState.columnFilters }}
              state={{ rowSelection, globalFilter, isLoading: loading }}
              onGlobalFilterChange={setGlobalFilter}
              getRowId={(row: any) => row?.id}
              muiTableBodyRowProps={(row: any) => getMuiTableBodyRowProps(row, saveCurrentUrl, currentUrl, navigate, summaryDocumentID)}
              renderTopToolbar={({ table }) => {
                const selectedCount = table.getSelectedRowModel().flatRows.length;
                if (!selectedCount) return null;

                return (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      pr: 2,
                      alignItems: 'center',
                      backgroundColor: '#e5f6fd',
                      width: '100%',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <ToolBarAlertBanner table={table} stackAlertBanner />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        startIcon={<EsgDownloadIcon />}
                        variant="contained"
                        sx={{ borderRadius: "8px", width: '140px' }}
                        disabled={selected?.length === 0 || isDownloading}
                        onClick={onDownloadClick}
                      >
                        {isDownloading ? "Downloading..." : "Download"}
                      </Button>
                      <Button
                        startIcon={<EsgDeleteIcon color="white" />}
                        variant="contained"
                        sx={{ borderRadius: "8px", width: '140px' }}
                        disabled={!selected?.length || userRole.isAuditor}
                        onClick={deleteDialogControls.open}
                        color="error"
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                );
              }}
              muiTableContainerProps={{
                sx: {
                  "& thead tr:first-of-type th:first-of-type": {
                    height: "47px",
                    paddingLeft: "20px",
                  },
                  "& thead tr:nth-of-type(2)": {
                    "& th": {
                      minWidth: 0,
                      "& > div > div > div": { minWidth: 0 },
                    },
                    "& th:first-of-type": {
                      minHeight: "46px",
                      paddingLeft: "20px",
                      mt: "auto",
                    },
                    "& th:nth-of-type(2)": {
                      minHeight: "46px",
                      mt: "auto",
                    }
                  },
                  "& tbody tr": {
                    "& td:first-of-type": {
                      minWidth: "fit-content",
                      maxWidth: tableData.length === 0 ? "auto" : "fit-content",
                      paddingLeft: "20px",
                      paddingRight: "0px",
                    },
                    "& td:nth-of-type(2)": {
                      minWidth: "fit-content",
                      maxWidth: tableData.length === 0 ? "auto" : "fit-content",
                      paddingLeft: "0px",
                      paddingRight: "25px",
                    }
                  },
                  "& tbody tr:first-of-type td > p": {
                    width: "100%",
                    justifyContent: "center",
                  },
                  height: "calc(100vh - 360px)",
                  border: "1px solid #D9D9D9",
                  borderRadius: "10px",
                  minWidth: "calc(100vw - 150px)",
                  width: "calc(100vw - 150px)",
                  maxWidth: "calc(100vw - 150px)",
                },
              }}
              muiTablePaperProps={{
                sx: {
                  borderRadius: "10px",
                  boxShadow: "none",
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  height: Object.keys(rowSelection).length > 0 ? 'auto' : '1px',
                  minHeight: '1px',
                  backgroundColor: Object.keys(rowSelection).length > 0 ? 'white' : 'transparent',
                  '& .MuiIconButton-root': {
                    display: 'none',
                  },
                },
              }}
              muiBottomToolbarProps={{
                sx: {
                  display: "none",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  lineHeight: "30px",
                  "& .MuiBadge-root": {
                    display: "none",
                  },
                },
              }}
              muiTableHeadRowProps={{
                sx: {
                  "& > th": {
                    paddingTop: 1,
                    paddingBottom: 1,
                    "& > div > div > div": {
                      minWidth: "max-content",
                    }
                  },
                  height: "auto",
                  alignItems: "center",
                  backgroundColor: "#F8FAFC",
                },
              }}
            />
            <Box sx={{
              display: "flex",
              justifyContent: "flex-end",
              my: "10px",
            }}>
              {tableData?.length > 0 && <Pagination
                page={uiPage}
                rowsCount={totalRows}
                rowsPerPage={size}
                rowsPerPageOptions={UTILITY_BILLS_TABLE.ROWS_PER_PAGE_OPTIONS}
                onPageChange={onPageChangeHandler}
                onRowsPerPageChange={onPageSizeChangeHandler}
              />}
            </Box>
          </>
        }
        {/* Save Filter Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Save Filter</DialogTitle>
          <DialogContent>
            <DialogContentText>Provide Filter Name!</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Filter Name"
              type="email"
              fullWidth
              variant="standard"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveButton}>Save Filter</Button>
          </DialogActions>
        </Dialog>
        <DeleteDialog
          isOpen={deleteDialogControls.isOpen}
          onCancel={deleteDialogControls.close}
          onDeleteClick={onDeleteClick}
          selectedCount={selected?.length}
          target="utility bill"
          title="Delete utility bills?"
        />
      </Box>
    )
  );
};
